"use client"

import { createContainer } from "unstated-next"
import { useState } from "react"

import { useCanvas } from "./canvas"

import { FEMALE_PRESETS, PresetType } from "../config"

import { useUser } from "@/app/actions/user"
import toast from "react-hot-toast"

export const toBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.readAsDataURL(blob)
  })
}

const GenerationContainer = createContainer(() => {
  const { userData, fetchUserData } = useUser()
  const { canvasRef, resetCanvas, isMasked, setIsMasked, resetTool } =
    useCanvas()

  const [selectedPreset, setSelectedPreset] = useState<PresetType>(
    FEMALE_PRESETS[0]
  )
  const [inputImageBase64, setInputImageBase64] = useState<string | null>(null)
  const [inputImageName, setInputImageName] = useState<string>("Untitled")
  const [imageNaturalSize, setImageNaturalSize] = useState({
    width: 0,
    height: 0,
  })
  const [outputImageBase64, setOutputImageBase64] = useState<string | null>(
    null
  )
  const [isCustomFaceMode, setIsCustomFaceMode] = useState(false)
  const [customFaceImageBase64, setCustomFaceImageBase64] = useState<
    string | null
  >(null)
  const [customFaceImageBase64Name, setCustomFaceImageBase64Name] = useState<
    string | null
  >(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [generationTime, setGenerationTime] = useState<number>(0)
  const [showHowToUse, setShowHowToUse] = useState<boolean>(false)

  const getBase64InpaintMask = () => {
    const brushCanvas = canvasRef?.current
    if (!brushCanvas) {
      return
    }

    const cnvs = document.createElement("canvas")
    cnvs.width = imageNaturalSize.width
    cnvs.height = imageNaturalSize.height

    const ctx = cnvs.getContext("2d")
    if (!ctx) {
      return
    }
    ctx.imageSmoothingQuality = "high"
    ctx.imageSmoothingEnabled = true

    ctx.fillStyle = "white"
    ctx.fillRect(0, 0, cnvs.width, cnvs.height)
    ctx.globalCompositeOperation = "destination-in"
    ctx.drawImage(brushCanvas, 0, 0, cnvs.width, cnvs.height)

    return cnvs.toDataURL("image/jpeg")
  }

  const generate = async () => {
    try {
      setIsLoading(true)
      if (!userData) {
        return
      }

      const res = await fetch(
        "https://py-temp-production.up.railway.app/gen/start",
        {
          method: "POST",
          body: JSON.stringify({
            preset: selectedPreset?.value,
            mask: getBase64InpaintMask(),
            image: inputImageBase64,
          }),
          headers: {
            Authorization: userData.id,
            "Content-Type": "application/json",
          },
        }
      )

      if (!res.ok) {
        throw new Error(`Generation failed: ${res.statusText}`)
      }

      const data = await res.json()
      console.log("Generation started with job_id:", data.image_id) // Added logging

      // Start polling for results
      const intervalId = setInterval(async () => {
        const statusRes = await fetch(
          `https://py-temp-production.up.railway.app/gen/status/${data.image_id}`,
          {
            headers: {
              Authorization: userData.id,
              "Content-Type": "application/json",
            },
          }
        )

        const statusData = await statusRes.json()

        if (statusData.status === "completed") {
          clearInterval(intervalId)
          setOutputImageBase64(statusData.base64_data)
          setIsLoading(false)

          // to update token value
          await fetchUserData()
        }
      }, 2000)
    } catch (error) {
      console.error("Generation error:", error)
      toast.error(
        "Generating prohibited content is not allowed on our platform try a different image."
      )
      setIsLoading(false)
    }
  }

  const reset = () => {
    setInputImageBase64(null)
    setOutputImageBase64(null)
    setCustomFaceImageBase64(null)
    setIsCustomFaceMode(false)
    setIsMasked(false)
    resetTool()
    resetCanvas()
  }

  return {
    inputImageBase64,
    setInputImageBase64,
    inputImageName,
    setInputImageName,
    imageNaturalSize,
    setImageNaturalSize,
    outputImageBase64,
    setOutputImageBase64,
    customFaceImageBase64,
    setCustomFaceImageBase64,
    customFaceImageBase64Name,
    setCustomFaceImageBase64Name,
    isCustomFaceMode,
    setIsCustomFaceMode,
    isLoading,
    setIsLoading,
    selectedPreset,
    setSelectedPreset,
    showHowToUse,
    setShowHowToUse,
    generationTime,
    setGenerationTime,
    isMasked,
    generate,
    reset,
  }
})

export const GenerationProvider = GenerationContainer.Provider
export const useGeneration = GenerationContainer.useContainer
